import i18next from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import backend, { HttpBackendOptions } from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

import {
  I18N_MISSING_VALUE_OPTIONS,
  LOCAL_STORAGE_USER_LOCALE,
  TranslationsTarget,
  buildCustomTranslationsUrl,
  GUEST_LOCALES,
  mapBrowserLanguageToSupported,
  withoutEmptyValues,
} from '@seaters/constants';
import { fetchTranslations } from '@seaters/data-access';

const loadCustomTranslations = async (
  language: string,
  slug: string,
  target: TranslationsTarget
) => {
  try {
    const customTranslationsUrl = buildCustomTranslationsUrl(
      slug,
      target,
      language
    );

    const response = await fetch(customTranslationsUrl, { cache: 'no-cache' });
    if (response.ok) {
      return await response.json();
    }
  } catch (error) {
    // 404s are expected when no custom translations exist
  }
  return null;
};

const getSlugFromUrl = (): string | null => {
  const url = window.location.pathname;
  const slug = url.split('/')[1];
  return slug || null;
};

const loadResources = async (language: string) => {
  const currentTarget = TranslationsTarget.GUEST;
  const slug = getSlugFromUrl();

  try {
    // Load default and custom translations in parallel
    const [defaultTranslations, customTranslations] = await Promise.all([
      fetchTranslations({
        lang: language,
        target: currentTarget,
      }).catch((error) => {
        console.error('Error loading default translations:', error);
        return null;
      }),
      slug
        ? loadCustomTranslations(language, slug, currentTarget)
        : Promise.resolve(null),
    ]);

    // Merge custom translations over default translations
    if (defaultTranslations && customTranslations) {
      return {
        ...defaultTranslations,
        ...withoutEmptyValues<Record<string, string>>(customTranslations),
      };
    }

    return defaultTranslations;
  } catch (error) {
    console.error('Error loading translations:', error);
    return null;
  }
};

const backendOptions: HttpBackendOptions = {
  loadPath: '{{lng}}|{{ns}}',
  customHeaders: {
    'Cache-Control': 'no-cache',
  },
  request: async (options, url, payload, callback) => {
    try {
      // Parse the language from the URL (format: "lng|ns")
      const language = url.split('|')[0];

      // Load requested language translations
      const translations = await loadResources(language);

      // The `en` leg of the fallback chain is i18next's own request, not ours:
      // it calls back in here with `lng=en` when a key is missing or empty.

      callback(null, {
        data: translations || {},
        status: translations ? 200 : 500,
      });
    } catch (e) {
      console.error('Error in i18next backend:', e);
      callback(null, {
        data: {},
        status: 500,
      });
    }
  },
};

i18next
  .use(
    new LanguageDetector(null, {
      lookupLocalStorage: LOCAL_STORAGE_USER_LOCALE,
      convertDetectedLanguage: (lng: string) =>
        mapBrowserLanguageToSupported(lng, GUEST_LOCALES),
    })
  )
  .use(initReactI18next)
  .use(backend)
  .init({
    backend: backendOptions,
    ...I18N_MISSING_VALUE_OPTIONS,
    react: {
      useSuspense: false,
    },
    detection: {
      order: ['localStorage', 'querystring', 'navigator'],
      lookupQuerystring: 'lang',
      convertDetectedLanguage: (lng: string) =>
        mapBrowserLanguageToSupported(lng, GUEST_LOCALES),
    },
    interpolation: {
      escapeValue: false,
    },
  });